package com.writerslab

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

internal fun formatTime(seconds: Int): String {
    val m = seconds / 60
    val s = seconds % 60
    return if (m > 0) "${m}m ${s}s" else "${s}s"
}

private fun formatScore(v: Double): String {
    val int = v.toInt()
    val dec = ((v - int) * 10).toInt()
    return "$int.$dec"
}

@Composable
fun AiInsightsSection(
    aiState: AnalysisState,
    credits: Int,
    isLoggedIn: Boolean,
    charCount: Int,
    onRunAi: () -> Unit,
    aiReport: AiReport?,
    aiStageName: String,
    aiStage: Int,
    aiTotalStages: Int,
    aiChunk: Int = 0,
    aiTotalChunks: Int = 0,
    aiEtaSeconds: Int? = null,
    aiElapsedSeconds: Int = 0,
    /** Free AI runs left today on short texts (server-tracked, UTC).
     *  Logged-in users get N/day on samples below the threshold. */
    freeShortRemaining: Int = 0,
    /** Character-count threshold below which the free-short rule
     *  applies. Chars rather than words — a single whitespace-free
     *  «word» of arbitrary length must not pass as a short text. */
    freeShortCharThreshold: Int = 30000,
    /** Rendered inside a framed CollapsibleBlock — drop this section's own card
     *  frame + header (chevron/title/status/help); the frame owns them. */
    bare: Boolean = false,
) {
    val s = LocalStrings.current
    val pricing = LocalPricing.current
    // Дефолт expanded: AI-отчёт это главная фича, прятать его за чевроном
    // нельзя по умолчанию. Пользователь может свернуть руками.
    var expanded by remember { mutableStateOf(true) }

    Div({
        style {
            if (!bare) {
                bg(WLColors.SurfaceContainerLow)
                property("border-radius", "8px")
                property("padding", "20px 20px 20px 16px")
                property("border-left", "4px solid ${WLColors.Secondary}")
            }
            flexCol(gapPx = 12)
            fillWidth()
            property("box-sizing", "border-box")
        }
    }) {
        // Шапка expand/collapse. «?»-кнопка живёт в outer-row снаружи
        // от clickable-области, чтобы клик по подсказке не сворачивал
        // секцию (и cursor: pointer не растекался на её зону).
        if (!bare) {
        Div({
            style {
                flexRow(gapPx = 12, align = AlignItems.Center)
                fillWidth()
            }
        }) {
            Div({
                style {
                    flexRow(gapPx = 12, align = AlignItems.Center)
                    property("flex", "1")
                    property("min-width", "0")
                    clickableCursor()
                }
                onClick { expanded = !expanded }
            }) {
                Span({
                    style {
                        fg(WLColors.Primary)
                        typography(TextLevel.TitleMedium)
                        property("width", "16px")
                        property("flex-shrink", "0")
                    }
                }) { Text(if (expanded) "▾" else "▸") }

                Span({
                    style {
                        typography(TextLevel.HeadlineSmall)
                        fg(WLColors.Primary)
                    }
                }) { Text(s.aiTitle) }

                // Краткий статус справа в свёрнутом виде.
                Span({
                    style {
                        typography(TextLevel.LabelSmall)
                        fg(when (aiState) {
                            AnalysisState.Done -> WLColors.Success
                            AnalysisState.Running -> WLColors.Tertiary
                            AnalysisState.Error -> WLColors.Error
                            AnalysisState.Idle -> WLColors.Outline
                        })
                        property("margin-left", "auto")
                    }
                }) {
                    Text(when (aiState) {
                        AnalysisState.Done -> s.aiStatusDone
                        AnalysisState.Running -> s.aiStatusRunning
                        AnalysisState.Error -> s.aiStatusError
                        AnalysisState.Idle -> s.aiStatusIdle
                    })
                }
            }
            BlockHelp(s.helpAiInsightsTitle, s.helpAiInsightsBody, BlockKind.AI, id = AnalyticsEvents.HelpBlock.AI_INSIGHTS)
        }
        }

        if (!bare && !expanded) return@Div

        // Свёртки: при ready просто рендерим AiReportContent внутри
        // expanded-области и возвращаемся; иначе показываем штатный
        // блок «запуск анализа» / прогресс.
        if (aiState == AnalysisState.Done && aiReport != null) {
            Div({ classes("wl-fade-in") }) {
                AiReportContent(
                    report = aiReport,
                    charCount = charCount,
                    credits = credits,
                    isLoggedIn = isLoggedIn,
                    onRerun = onRunAi,
                )
            }
            return@Div
        }

        val cost = calcAiCost(charCount, pricing.analysisCharsPerCredit)
        // Free-short eligibility: small text + logged in + daily limit not
        // hit. Server is the source of truth — front-end mirrors it for
        // labelling (button text, pricing line). The actual charge decision
        // happens server-side on `/pipeline/start`.
        val isShortByChars = charCount in 1 until freeShortCharThreshold
        val isFreeShort = isLoggedIn && isShortByChars && freeShortRemaining > 0
        // Anonymous user with a short text — clicking the button takes them
        // through Keycloak; after sign-in the same text becomes free under
        // the daily quota. We surface that *before* they click so the
        // button doesn't lie about a "1 credit" charge that never happens.
        val isAnonFreeShortEligible = !isLoggedIn && isShortByChars

        // Price chip + pricing hint only describe the «if you start it now»
        // proposition — once the pipeline is in flight the user has already
        // paid, so showing «Получить разбор · N» next to a progress bar
        // reads as a duplicate (and stale) call to action. Restrict the
        // pre-launch chrome to Idle / Error states.
        if (aiState == AnalysisState.Idle || aiState == AnalysisState.Error) {
            Div({
                style {
                    flexRow(gapPx = 8)
                    fillWidth()
                }
            }) {
                val showGreenBadge = isFreeShort || isAnonFreeShortEligible
                if (isLoggedIn || isAnonFreeShortEligible) {
                    Span({
                        style {
                            property("background-color",
                                if (showGreenBadge) "rgba(111, 207, 151, 0.15)"
                                else "rgba(245, 185, 71, 0.15)")
                            fg(if (showGreenBadge) WLColors.Success else WLColors.Secondary)
                            typography(TextLevel.LabelSmall)
                            property("padding", "3px 8px")
                            property("border-radius", "120px")
                        }
                    }) {
                        Text(when {
                            isFreeShort -> s.aiFreeShortBadge(freeShortRemaining)
                            isAnonFreeShortEligible -> s.aiFreeShortBadgeAnon
                            else -> s.aiUnlock(cost)
                        })
                    }
                }
            }
            Span({ style { typography(TextLevel.BodySmall); fg(WLColors.OnSurfaceVariant) } }) {
                Text(when {
                    isFreeShort -> s.aiFreeShortHint(freeShortRemaining) + " · " + s.aiPreviewTimeHint
                    isAnonFreeShortEligible -> s.aiFreeShortHintAnon + " · " + s.aiPreviewTimeHint
                    else -> s.aiPricing(pricing.analysisCharsPerCredit) + " · " + s.aiPreviewTimeHint
                })
            }
        }

        // Primary CTA sits ABOVE the tab strip so it reads as the action
        // for the whole AI section. With the button below the tabs the
        // reader perceived it as belonging to the currently-selected tab.
        when (aiState) {
            AnalysisState.Idle, AnalysisState.Error -> {
                val canAfford = !isLoggedIn || isFreeShort || credits >= cost
                var hintTick by remember { mutableIntStateOf(0) }
                LaunchedEffect(hintTick) {
                    if (hintTick > 0) {
                        delay(4000)
                        hintTick = 0
                    }
                }
                // Block container with text-align: center; icon flows
                // inline after the label so when the text wraps onto a
                // second line the icon sticks to the last word, not
                // floating in the middle of the row.
                Div({
                    style {
                        property("background-color", WLColors.Secondary)
                        fg(WLColors.OnSecondary)
                        typography(TextLevel.LabelLarge)
                        property("padding", "14px 16px")
                        property("border-radius", "120px")
                        property("text-align", "center")
                        clickableCursor()
                        property("box-sizing", "border-box")
                        fillWidth()
                    }
                    onClick {
                        if (!isLoggedIn) onRunAi()
                        else if (canAfford) onRunAi()
                        else hintTick++
                    }
                }) {
                    Text(when {
                        isFreeShort -> s.aiUnlockFree
                        isAnonFreeShortEligible -> s.aiUnlockFreeAfterSignIn
                        else -> s.aiUnlock(cost)
                    })
                    if (!isFreeShort && !isAnonFreeShortEligible) {
                        Text("\u00A0")
                        DragonIcon(sizePx = 16, tint = DragonTint.DARK)
                    }
                }
                if (!canAfford && hintTick > 0) {
                    Span({
                        classes("wl-fade-in")
                        style {
                            typography(TextLevel.BodySmall)
                            fg(WLColors.Error)
                            property("padding-top", "4px")
                            property("text-align", "center")
                        }
                    }) { Text(s.aiNoCredits) }
                }
                // Short-text free-quota signal — same style as the promo
                // tabs' FreeQuotaHint. Skipped when the current upload
                // is already eligible (the chip + hint above the CTA
                // are saying it more specifically); otherwise visible
                // always so users learn the rule before they bother
                // uploading a long text.
                if (!isFreeShort && !isAnonFreeShortEligible) {
                    AiFreeShortQuotaHint(
                        isLoggedIn = isLoggedIn,
                        freeShortRemaining = freeShortRemaining,
                        freeShortCharThreshold = freeShortCharThreshold,
                    )
                }
            }
            AnalysisState.Running -> {
                AiProgress(
                    stage = aiStage,
                    totalStages = aiTotalStages,
                    chunk = aiChunk,
                    totalChunks = aiTotalChunks,
                    etaSeconds = aiEtaSeconds,
                    elapsedSeconds = aiElapsedSeconds,
                )
            }
            else -> {}
        }

        Span({
            style {
                typography(TextLevel.LabelLarge)
                fg(WLColors.OnSurfaceVariant)
                property("text-transform", "uppercase")
                property("letter-spacing", "0.05em")
                property("padding-top", "4px")
            }
        }) { Text(s.aiPreviewWhatYouGet) }

        LockedTabsPreview()
    }
}

/**
 * Small line under the AI CTA describing the «short texts run free»
 * rule and the user's remaining daily quota. Three flavours — anon
 * (call to sign in), logged-in with quota left (green), logged-in
 * with quota exhausted (neutral grey). Hidden if the backend hasn't
 * reported a word threshold yet (briefly true on first paint before
 * /me lands). Mirrors PromoMaterialsSection's FreeQuotaHint shape.
 */
@Composable
private fun AiFreeShortQuotaHint(
    isLoggedIn: Boolean,
    freeShortRemaining: Int,
    freeShortCharThreshold: Int,
) {
    if (freeShortCharThreshold <= 0) return
    val s = LocalStrings.current
    val text: String
    val color: String
    when {
        !isLoggedIn -> {
            text = s.aiFreeShortQuotaAnon(freeShortCharThreshold)
            color = WLColors.Tertiary
        }
        freeShortRemaining > 0 -> {
            text = s.aiFreeShortQuotaRemaining(freeShortRemaining, freeShortCharThreshold)
            color = WLColors.Tertiary
        }
        else -> {
            text = s.aiFreeShortQuotaExhausted(freeShortCharThreshold)
            color = WLColors.OnSurfaceVariant
        }
    }
    Span({
        style {
            typography(TextLevel.LabelSmall)
            fg(color)
            property("text-align", "center")
            property("padding-top", "2px")
        }
    }) { Text(text) }
}

@Composable
private fun AiProgress(
    stage: Int,
    totalStages: Int,
    chunk: Int,
    totalChunks: Int,
    etaSeconds: Int?,
    elapsedSeconds: Int,
) {
    val s = LocalStrings.current
    // Каждый этап — равная доля шкалы (1/totalStages); внутри этапа бар плавно
    // растёт по chunk/totalChunks. Работает для любого числа этапов. Прежний
    // вариант двигал внутри-этапную долю только на этапе 1 по жёстко зашитой
    // 7-этапной кривой — на 2-этапном пайплайне бар замирал между этапами.
    val progress = run {
        val stages = totalStages.coerceAtLeast(1)
        val span = 1f / stages
        val completed = (stage - 1).coerceAtLeast(0) * span
        val inStage = if (totalChunks > 0) chunk.toFloat() / totalChunks else 0f
        completed + inStage * span
    }
    // Determinate from the start. The first chunk usually arrives in a few
    // hundred ms, so flipping from indeterminate-running to value=0 looks
    // like a jitter — better to just show 0%.
    Div({
        classes("wl-ai-progress")
        style { fillWidth() }
    }) {
        MdLinearProgress(value = progress.coerceIn(0f, 1f))
    }

    val messages = s.loadingMessages
    val shuffled = remember(messages) { messages.shuffled() }
    var msgIndex by remember { mutableIntStateOf(0) }
    LaunchedEffect(shuffled) {
        while (true) {
            delay(5000)
            if (shuffled.isNotEmpty()) msgIndex = (msgIndex + 1) % shuffled.size
        }
    }

    Div({
        style {
            flexRow(gapPx = 8)
            fillWidth()
            property("justify-content", "space-between")
            alignItems(AlignItems.FlexStart)
        }
    }) {
        Span({ style { typography(TextLevel.BodyMedium); fg(WLColors.OnSurface); property("flex", "1") } }) {
            Text(if (shuffled.isEmpty()) "…" else shuffled[msgIndex])
        }
        Div({
            style {
                flexCol()
                property("align-items", "flex-end")
            }
        }) {
            if (etaSeconds != null && etaSeconds > 0) {
                Span({ style { typography(TextLevel.BodySmall); fg(WLColors.OnSurfaceVariant) } }) {
                    Text("~ ${formatTime(etaSeconds)}")
                }
            }
            if (elapsedSeconds > 0) {
                Span({ style { typography(TextLevel.LabelSmall); fg(WLColors.OnSurfaceVariant) } }) {
                    Text(formatTime(elapsedSeconds))
                }
            }
        }
    }
}

/** Pre-run preview that mirrors the post-run layout: same tab strip,
 *  same content panel, just placeholder copy per tab instead of real
 *  generated content. The shape of what the user is about to pay for
 *  is visible up front — no surprise jump to a different layout after
 *  unlock. */
@Composable
private fun LockedTabsPreview() {
    var selectedTab by remember { mutableStateOf(ReportTab.REVIEW) }
    val s = LocalStrings.current

    Div({ style { flexCol(gapPx = 12); fillWidth() } }) {
        // Tab strip — same outline-only treatment as the Promo section:
        // strip sits flat on the outer surface, a thin OutlineVariant
        // line under the row carries the gold underline of the selected
        // tab. No wrapping box-with-bg → no nested «card on card» feel.
        Div({
            style {
                fillWidth()
                property("box-sizing", "border-box")
                property("overflow-x", "auto")
            }
        }) {
            Div({
                style {
                    flexRow(gapPx = 2)
                    property("min-width", "max-content")
                    property("border-bottom", "1px solid ${WLColors.OutlineVariant}")
                }
            }) {
                ReportTab.entries.forEach { tab ->
                    val isSelected = tab == selectedTab
                    // Gold underline marks the active tab — mirrors the
                    // promo tab strip style; no bg fill.
                    Div({
                        style {
                            property("padding", "10px 14px 8px 14px")
                            typography(TextLevel.LabelLarge)
                            property("white-space", "nowrap")
                            property(
                                "border-bottom",
                                if (isSelected) "2px solid ${WLColors.Secondary}"
                                else "2px solid transparent"
                            )
                            fg(if (isSelected) WLColors.Secondary else WLColors.OnSurfaceVariant)
                            clickableCursor()
                        }
                        onClick { selectedTab = tab }
                    }) {
                        Text(tab.labelText(s))
                    }
                }
            }
        }

        // Placeholder body for the selected tab. Outline-only frame:
        // shares the section's surface so there is no third light
        // surface stacking on top.
        androidx.compose.runtime.key(selectedTab) {
            Div({
                classes("wl-fade-in")
                style {
                    fillWidth()
                    property("box-sizing", "border-box")
                    property("padding", "16px")
                    property("border", "1px solid ${WLColors.OutlineVariant}")
                    property("border-radius", "8px")
                    flexCol(gapPx = 10)
                }
            }) {
                // What's in this section (concrete deliverable).
                Span({
                    style {
                        typography(TextLevel.BodyLarge)
                        fg(WLColors.OnSurface)
                    }
                }) { Text(selectedTab.previewDesc(s)) }
                // Why a writer needs it (problem it solves).
                Span({
                    style {
                        typography(TextLevel.BodyMedium)
                        fg(WLColors.OnSurfaceVariant)
                    }
                }) { Text(selectedTab.previewSample(s)) }
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Unlocked report — score card, tabs, content
// ──────────────────────────────────────────────────────────────────────

private enum class ReportTab(
    val labelText: (Strings) -> String,
    /** Placeholder description shown in the tab body before the AI run
     *  has happened. Same string the pre-run preview cards used to
     *  carry, repurposed so the preview can sit inside the tab layout
     *  instead of next to it. */
    val previewDesc: (Strings) -> String,
    /** Faux sample line under the description — concrete enough to give
     *  the reader a sense of voice without making any promises about
     *  the specific output. */
    val previewSample: (Strings) -> String,
) {
    REVIEW({ it.tabReview }, { it.aiPreviewReviewDesc }, { it.aiPreviewReviewSample }),
    STRENGTHS({ it.tabStrengths }, { it.aiPreviewStrengthsDesc }, { it.aiPreviewStrengthsSample }),
    PLOT({ it.tabPlot }, { it.aiPreviewPlotDesc }, { it.aiPreviewPlotSample }),
    CHARACTERS({ it.tabCharacters }, { it.aiPreviewCharactersDesc }, { it.aiPreviewCharactersSample }),
    LANGUAGE({ it.tabLanguage }, { it.aiPreviewLanguageDesc }, { it.aiPreviewLanguageSample }),
    RECOMMENDATIONS({ it.tabRecommendations }, { it.aiPreviewRecommendationsDesc }, { it.aiPreviewRecommendationsSample }),
    UNRESOLVED({ it.tabUnresolved }, { it.aiPreviewUnresolvedDesc }, { it.aiPreviewUnresolvedSample }),
}

@Composable
private fun AiReportContent(
    report: AiReport,
    charCount: Int,
    credits: Int,
    isLoggedIn: Boolean,
    onRerun: () -> Unit,
) {
    var selectedTab by remember { mutableStateOf(ReportTab.REVIEW) }
    val s = LocalStrings.current
    val pricing = LocalPricing.current
    val analytics = LocalAnalyticsEvents.current

    Div({
        style {
            flexCol(gapPx = 12)
            fillWidth()
        }
    }) {
        // Score card is rendered separately in the sidebar via
        // ResultsArea#aiSidebarSlot, matching the original wasm layout.

        // Tab strip — same outline-only treatment as Promo / LockedTabsPreview.
        Div({
            style {
                fillWidth()
                property("box-sizing", "border-box")
                property("overflow-x", "auto")
            }
        }) {
            Div({
                style {
                    flexRow(gapPx = 2)
                    property("min-width", "max-content")
                    property("border-bottom", "1px solid ${WLColors.OutlineVariant}")
                }
            }) {
                ReportTab.entries.forEach { tab ->
                    val isSelected = tab == selectedTab
                    Div({
                        style {
                            property("padding", "10px 14px 8px 14px")
                            typography(TextLevel.LabelLarge)
                            property("white-space", "nowrap")
                            property(
                                "border-bottom",
                                if (isSelected) "2px solid ${WLColors.Secondary}"
                                else "2px solid transparent"
                            )
                            fg(if (isSelected) WLColors.Secondary else WLColors.OnSurfaceVariant)
                            clickableCursor()
                        }
                        onClick {
                            if (tab != selectedTab) {
                                analytics.aiTabSwitched(tab.name)
                                selectedTab = tab
                            }
                        }
                    }) {
                        Text(tab.labelText(s))
                    }
                }
            }
        }

        // Tab body — `key(selectedTab)` forces the inner Div to remount on
        // tab change, which re-triggers the fade-in animation. Outline-only
        // frame, shares the outer surface so no nested light card.
        androidx.compose.runtime.key(selectedTab) {
            Div({
                classes("wl-fade-in")
                style {
                    fillWidth()
                    property("box-sizing", "border-box")
                    property("padding", "16px")
                    property("border", "1px solid ${WLColors.OutlineVariant}")
                    property("border-radius", "8px")
                    flexCol(gapPx = 8)
                }
            }) {
                when (selectedTab) {
                    ReportTab.REVIEW -> ReviewContent(report.review, report.betaReader)
                    ReportTab.STRENGTHS -> StrengthsContent(report.strengths, report.betaReader)
                    ReportTab.PLOT -> PlotContent(report.plot)
                    ReportTab.CHARACTERS -> CharactersContent(
                        report.characters,
                        report.relationships,
                        report.charactersAssessment,
                    )
                    ReportTab.LANGUAGE -> LanguageContent(report.language)
                    ReportTab.RECOMMENDATIONS -> RecommendationsContent(report.recommendations)
                    ReportTab.UNRESOLVED -> UnresolvedContent(report.unresolvedPlots)
                }
            }
        }

        // Made before the text was edited in the reader: say so above the rerun.
        if (report.textChanged) {
            Div({
                style {
                    typography(TextLevel.BodySmall)
                    fg(WLColors.OnSurfaceVariant)
                    property("text-align", "center")
                }
            }) { Text(s.aiTextChanged(report.textEditedAt?.let { formatEditDate(it) }?.ifBlank { null })) }
        }
        // Rerun button (outlined)
        val cost = calcAiCost(charCount, pricing.analysisCharsPerCredit)
        val canAfford = !isLoggedIn || credits >= cost
        var hintTick by remember { mutableIntStateOf(0) }
        LaunchedEffect(hintTick) {
            if (hintTick > 0) {
                delay(4000)
                hintTick = 0
            }
        }
        Div({
            style {
                fillWidth()
                property("padding", "12px 16px")
                property("border-radius", "120px")
                property("text-align", "center")
                property("box-sizing", "border-box")
                typography(TextLevel.BodyMedium)
                border("1px", "solid", WLColors.Secondary)
                fg(WLColors.Secondary)
                clickableCursor()
            }
            onClick {
                if (canAfford) onRerun()
                else hintTick++
            }
        }) {
            Text(s.aiRerun(cost))
            Text("\u00A0")
            // Gold outline button, gold text — match the icon to the
            // surrounding gold tint.
            DragonIcon(sizePx = 16, tint = DragonTint.GOLD)
        }
        if (!canAfford && hintTick > 0) {
            Span({
                classes("wl-fade-in")
                style {
                    typography(TextLevel.BodySmall)
                    fg(WLColors.Error)
                    property("padding-top", "4px")
                    property("text-align", "center")
                }
            }) { Text(s.aiNoCredits) }
        }
    }
}

@Composable
fun AiScoreCard(score: AiScore?) {
    val s = LocalStrings.current
    // `score == null` → the deep analysis hasn't run yet: render the SAME card
    // with dashes instead of numbers (a preview of what the run produces).
    // `text_quality` is dropped — it's the static metric, shown elsewhere; the
    // headline is the mean of what's left so it can't disagree with the bars.
    val aiComponents = score?.components?.filterKeys { it != "text_quality" } ?: emptyMap()
    val finalScore: Double? = when {
        score == null -> null
        aiComponents.isNotEmpty() -> aiComponents.values.average()
        else -> score.final
    }
    val qColor = when {
        finalScore == null -> WLColors.OnSurfaceVariant
        finalScore >= 7 -> WLColors.Success
        finalScore >= 4 -> "#FACC15"
        else -> WLColors.Error
    }
    // Gold left stripe marks this as the paid-analysis surface.
    Div({
        style {
            bg(WLColors.SurfaceContainerLow)
            property("border-radius", "8px")
            property("padding", "20px 20px 20px 16px")
            property("border-left", "4px solid ${WLColors.Secondary}")
            flexCol(gapPx = 12)
            fillWidth()
            property("box-sizing", "border-box")
            property("position", "relative")
        }
    }) {
        BlockHelpCorner(s.helpAiScoreTitle, s.helpAiScoreBody, BlockKind.AI, id = AnalyticsEvents.HelpBlock.AI_SCORE)
        Div({
            style {
                flexRow(gapPx = 8, align = AlignItems.Baseline)
            }
        }) {
            Span({
                classes("wl-ai-score")
                style {
                    property("font-size", "42px")
                    property("font-weight", "600")
                    property("line-height", "1")
                    fg(qColor)
                }
            }) { Text(finalScore?.let { formatScore(it) } ?: "—") }
            Span({ style { typography(TextLevel.BodyMedium); fg(WLColors.OnSurfaceVariant) } }) { Text("/10") }
            Div({ style { property("width", "12px") } })
            Span({ style { typography(TextLevel.TitleMedium); fg(WLColors.OnSurface) } }) { Text(s.aiScore) }
        }
        // Backend ships component keys as English snake_case (plot,
        // characters, language). The aspect-scored prompts also produce
        // `beta_reader` for legacy reports. Map each to its localized
        // label; unknown keys fall through with the raw key.
        val labels = mapOf(
            "plot" to s.tabPlot,
            "characters" to s.tabCharacters,
            "language" to s.tabLanguage,
            "beta_reader" to s.storyAndCharacters,
        )
        // Real bars, or a placeholder set (plot / characters / language)
        // with dashes when the run hasn't happened yet.
        val bars: List<Pair<String, Double?>> =
            if (aiComponents.isNotEmpty()) aiComponents.map { (k, v) -> (labels[k] ?: k) to (v as Double?) }
            else listOf(s.tabPlot to null, s.tabCharacters to null, s.tabLanguage to null)
        Div({ style { flexCol(gapPx = 8) } }) {
            bars.forEach { (label, value) -> ComponentBar(label = label, value = value) }
        }
    }
}

/** One sub-score row: label + colour-coded bar + value. The bar fills
 *  to value/10 and switches between green / amber / red at 7 and 4.
 *  Visually richer than the previous «label  9.0/10» plain text row. */
@Composable
private fun ComponentBar(label: String, value: Double?) {
    val pct = if (value != null) (value / 10.0).coerceIn(0.0, 1.0) else 0.0
    val color = when {
        value == null -> "rgba(74, 71, 84, 0.5)"
        value >= 7 -> WLColors.Success
        value >= 4 -> "#FACC15"
        else       -> WLColors.Error
    }
    Div({ style { flexCol(gapPx = 4); fillWidth() } }) {
        Div({ style { flexRow(gapPx = 8); fillWidth() } }) {
            Span({
                style {
                    typography(TextLevel.BodySmall)
                    fg(WLColors.OnSurfaceVariant)
                    property("flex", "1")
                    property("min-width", "0")
                }
            }) { Text(label) }
            Span({
                style {
                    typography(TextLevel.BodySmall)
                    fg(WLColors.OnSurface)
                }
            }) { Text(if (value != null) "${formatScore(value)}/10" else "—/10") }
        }
        Div({
            style {
                fillWidth()
                property("height", "6px")
                property("border-radius", "3px")
                bg("rgba(74, 71, 84, 0.5)")
                property("overflow", "hidden")
            }
        }) {
            Div({
                style {
                    property("width", "${(pct * 100).toInt()}%")
                    property("height", "100%")
                    bg(color)
                }
            })
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Tab content
// ──────────────────────────────────────────────────────────────────────

@Composable
private fun ReviewContent(review: String, beta: AiBetaReader) {
    // Бета-ридер overall — короткая прозовая характеристика «для кого
    // эта книга», 3–4 предложения. Рендерится первой, ставит тон
    // перед основной рецензией.
    if (beta.overall.isNotBlank()) {
        Div({
            style {
                fillWidth()
                property("padding", "8px 0 12px")
                property("border-bottom", "1px solid ${WLColors.OutlineVariant}")
                property("margin-bottom", "4px")
            }
        }) {
            Span({ style { typography(TextLevel.BodyLarge); fg(WLColors.OnSurface) } }) {
                Text(beta.overall)
            }
        }
    }

    if (review.isNotBlank()) {
        // Single prose block — no lifted backing. The bordered tab-content
        // frame already groups the review; an inner surface tier would be
        // a redundant «card on card» that doesn't separate anything.
        MarkdownBlock(
            source = review,
            attrs = {
                classes("wl-md")
                style { markdownTheme() }
            },
        )
    }
}

@Composable
private fun StrengthsContent(strengths: AiStrengths, beta: AiBetaReader) {
    val s = LocalStrings.current
    // Advocate craft strengths + beta-reader strengths — «что работает».
    // emotionalImpact / memorable are intentionally NOT shown: they're
    // scene-by-scene recaps (episodes tagged with chapter numbers), which read
    // as a catalogue of notable scenes rather than «what the book does well».
    // The LLM still returns them; they're just no longer rendered here.
    val allStrengths = strengths.craft + beta.strengths
    val anyStrengths = allStrengths.isNotEmpty() ||
        strengths.worldAndAtmosphere.isNotBlank()
    if (anyStrengths) {
        Span({
            style {
                typography(TextLevel.TitleMedium)
                fg(WLColors.Success)
                property("padding-top", "12px")
            }
        }) { Text(s.strengths) }
        allStrengths.forEach { line ->
            StrengthItem(line, WLColors.Success, "+")
        }
        if (strengths.worldAndAtmosphere.isNotBlank()) {
            StrengthItem(strengths.worldAndAtmosphere, WLColors.Success, "+")
        }
    }

    // Бета-ридер weaknesses — «что нужно править». Жил раньше на
    // отдельной BETA-вкладке (удалена 2026-05-25); красный блок
    // под зелёными достоинствами.
    if (beta.weaknesses.isNotEmpty()) {
        Span({
            style {
                typography(TextLevel.TitleMedium)
                fg(WLColors.Error)
                property("padding-top", "12px")
            }
        }) { Text(s.keyWeaknesses) }
        beta.weaknesses.forEach { line ->
            StrengthItem(line, WLColors.Error, "−")
        }
    }
}

/**
 * Renders a list of yes/no/n_a answers with the question label and
 * evidence underneath. Used by all 3 scoring tabs (plot, characters,
 * language) and the beta-reader «first impression» block.
 *
 * Question labels come from `s.aiScoringQuestions[id]` so the UI is
 * bilingual without the backend shipping per-locale text.
 */
@Composable
private fun QuestionListContent(assessment: AiCategoryAssessment) {
    val s = LocalStrings.current
    if (assessment.questions.isEmpty()) return

    // Summary header: «8/12 «да» · 6.7/10»
    Div({
        style {
            fillWidth()
            property("padding", "4px 0 8px")
            property("border-bottom", "1px solid ${WLColors.OutlineVariant}")
        }
    }) {
        Span({ style { typography(TextLevel.LabelLarge); fg(WLColors.Primary) } }) {
            Text(s.aiCategoryStats(assessment.yesCount, assessment.applicable, assessment.numeric))
        }
    }

    assessment.questions.forEachIndexed { index, q ->
        val (mark, color) = when (q.answer) {
            "yes"  -> "✓" to WLColors.Success
            "no"   -> "✗" to WLColors.Error
            else   -> "·" to WLColors.OnSurfaceVariant
        }
        val label = s.aiScoringQuestions[q.id] ?: q.id
        Div({
            style {
                fillWidth()
                property("box-sizing", "border-box")
                property("padding", "10px 0")
                if (index > 0) {
                    property("border-top", "1px solid ${WLColors.OutlineVariant}")
                }
                flexCol(gapPx = 4)
            }
        }) {
            Div({ style { flexRow(gapPx = 10, align = AlignItems.FlexStart); fillWidth() } }) {
                Span({
                    style {
                        typography(TextLevel.TitleMedium)
                        fg(color)
                        property("min-width", "20px")
                        property("line-height", "1.4")
                    }
                }) { Text(mark) }
                Span({
                    style {
                        typography(TextLevel.BodyMedium)
                        fg(WLColors.OnSurface)
                        property("flex", "1")
                    }
                }) { Text(label) }
            }
            if (q.evidence.isNotBlank() && q.answer != "n_a") {
                Span({
                    style {
                        typography(TextLevel.BodySmall)
                        fg(WLColors.OnSurfaceVariant)
                        property("padding-left", "30px")
                    }
                }) { Text(q.evidence) }
            }
        }
    }
}


@Composable
private fun CharactersContent(
    characters: List<AiCharacter>,
    relationships: List<AiRelationship>,
    assessment: AiCategoryAssessment,
) {
    val s = LocalStrings.current
    characters.forEachIndexed { index, ch ->
        val roleColor = when (ch.role.lowercase()) {
            "protagonist" -> WLColors.Primary
            "antagonist" -> WLColors.Error
            else -> WLColors.OnSurfaceVariant
        }
        // No lifted card bg, no role-coloured left accent — together
        // they read as «every block is highlighted in white» against
        // the dark frame. Role is now carried only by the coloured
        // pill next to the name, and characters separate via padding
        // + a thin divider line above each one except the first.
        Div({
            style {
                fillWidth()
                property("box-sizing", "border-box")
                property("padding", "12px 0")
                if (index > 0) {
                    property("border-top", "1px solid ${WLColors.OutlineVariant}")
                }
                flexCol(gapPx = 6)
            }
        }) {
            Div({ style { flexRow(gapPx = 8); fillWidth() } }) {
                Span({ style { typography(TextLevel.TitleMedium); fg(WLColors.OnSurface) } }) { Text(ch.name) }
                if (ch.role.isNotBlank()) {
                    Span({
                        style {
                            property("background-color", "rgba(165, 174, 247, 0.12)")
                            fg(roleColor)
                            typography(TextLevel.LabelSmall)
                            property("padding", "3px 8px")
                            property("border-radius", "120px")
                        }
                    }) { Text(ch.role) }
                }
            }
            if (ch.description.isNotBlank()) {
                Span({ style { typography(TextLevel.BodyMedium); fg(WLColors.OnSurfaceVariant) } }) { Text(ch.description) }
            }
            if (ch.arc.isNotBlank()) {
                Span({ style { typography(TextLevel.BodySmall); fg(WLColors.OnSurfaceVariant) } }) {
                    Text("${s.arcPrefix}${ch.arc}")
                }
            }
        }
    }
    if (relationships.isNotEmpty()) {
        Span({
            style {
                typography(TextLevel.TitleMedium)
                fg(WLColors.Primary)
                property("padding-top", "8px")
            }
        }) { Text(s.relationships) }
        relationships.forEachIndexed { index, rel ->
            Div({
                style {
                    fillWidth()
                    property("box-sizing", "border-box")
                    property("padding", "10px 0")
                    if (index > 0) {
                        property("border-top", "1px solid ${WLColors.OutlineVariant}")
                    }
                    flexCol(gapPx = 4)
                }
            }) {
                Span({ style { typography(TextLevel.TitleSmall); fg(WLColors.OnSurface) } }) {
                    Text(rel.characters.joinToString(" - "))
                }
                Div({ style { flexRow(gapPx = 8); fillWidth() } }) {
                    Span({
                        style {
                            property("background-color", "rgba(165, 174, 247, 0.12)")
                            fg(WLColors.Primary)
                            typography(TextLevel.LabelSmall)
                            property("padding", "2px 6px")
                            property("border-radius", "120px")
                        }
                    }) { Text(rel.type) }
                    Span({ style { typography(TextLevel.BodySmall); fg(WLColors.OnSurfaceVariant) } }) {
                        Text(rel.description)
                    }
                }
            }
        }
    }
    // Character assessment from the dedicated LLM pass — yes/no answers
    // by atomic question. The earlier per-aspect comment block has been
    // replaced by the universal QuestionListContent: same shape as the
    // other scoring tabs (plot / language).
    if (assessment.questions.isNotEmpty()) {
        Div({
            style {
                fillWidth()
                property("border-top", "1px solid ${WLColors.OutlineVariant}")
                property("padding-top", "12px")
                property("margin-top", "8px")
                flexCol(gapPx = 4)
            }
        }) {
            Span({ style { typography(TextLevel.TitleMedium); fg(WLColors.Primary) } }) {
                Text(s.characterAssessment)
            }
        }
        QuestionListContent(assessment)
    }
}

@Composable
private fun LanguageContent(language: AiCategoryAssessment) {
    QuestionListContent(language)
}

@Composable
private fun StrengthItem(text: String, color: String, prefix: String) {
    Div({
        style {
            flexRow(gapPx = 8, align = AlignItems.FlexStart)
            property("padding", "3px 0")
        }
    }) {
        Span({
            style {
                typography(TextLevel.BodyMedium)
                fg(color)
                property("min-width", "20px")
            }
        }) { Text(prefix) }
        Span({ style { typography(TextLevel.BodyMedium); fg(color) } }) { Text(text) }
    }
}

@Composable
private fun PlotContent(plot: AiCategoryAssessment) {
    QuestionListContent(plot)
}

@Composable
private fun RecommendationsContent(recs: List<AiRecommendation>) {
    val s = LocalStrings.current
    recs.forEachIndexed { index, rec ->
        Div({
            style {
                fillWidth()
                property("box-sizing", "border-box")
                property("padding", "12px 0")
                if (index > 0) {
                    property("border-top", "1px solid ${WLColors.OutlineVariant}")
                }
                flexRow(gapPx = 12, align = AlignItems.FlexStart)
            }
        }) {
            Span({
                style {
                    typography(TextLevel.TitleMedium)
                    fg("#FACC15")
                    property("min-width", "32px")
                    property("text-align", "center")
                }
            }) { Text("${index + 1}") }
            Div({ style { flexCol(gapPx = 4); property("flex", "1") } }) {
                Span({ style { typography(TextLevel.TitleSmall); fg(WLColors.OnSurface) } }) { Text(rec.issue) }
                if (rec.where.isNotBlank()) {
                    Span({ style { typography(TextLevel.BodySmall); fg(WLColors.OnSurfaceVariant) } }) {
                        Text("${s.wherePrefix}${rec.where}")
                    }
                }
                if (rec.suggestion.isNotBlank()) {
                    Span({ style { typography(TextLevel.BodySmall); fg(WLColors.Success) } }) {
                        Text("${s.tipPrefix}${rec.suggestion}")
                    }
                }
            }
        }
    }
}

@Composable
private fun UnresolvedContent(plots: List<AiUnresolved>) {
    val s = LocalStrings.current
    // Empty state — «everything resolved» is itself a noteworthy result
    // for the writer, not a non-event. Green check + a slightly long
    // explanatory line, no backing surface, same flat treatment as the
    // rest of the AI tab content.
    if (plots.isEmpty()) {
        Div({
            style {
                fillWidth()
                flexCol(gapPx = 8)
                property("padding", "8px 0")
            }
        }) {
            Div({ style { flexRow(gapPx = 10, align = AlignItems.Center) } }) {
                Span({
                    style {
                        typography(TextLevel.TitleMedium)
                        fg(WLColors.Success)
                        property("line-height", "1")
                    }
                }) { Text("✓") }
                Span({
                    style {
                        typography(TextLevel.TitleSmall)
                        fg(WLColors.OnSurface)
                    }
                }) { Text(s.unresolvedEmptyTitle) }
            }
            Span({
                style {
                    typography(TextLevel.BodyMedium)
                    fg(WLColors.OnSurfaceVariant)
                }
            }) { Text(s.unresolvedEmptyHint) }
        }
        return
    }
    // Plot threads as a flat list — no lifted backing. Each item is a
    // red dot + element/issue, separated by a thin divider so the list
    // reads as distinct entries rather than a wall of text.
    Div({ style { fillWidth(); flexCol(gapPx = 0) } }) {
        plots.forEachIndexed { index, u ->
            Div({
                style {
                    flexRow(gapPx = 10, align = AlignItems.FlexStart)
                    fillWidth()
                    property("padding", "10px 0")
                    if (index > 0) {
                        property("border-top", "1px solid ${WLColors.OutlineVariant}")
                    }
                }
            }) {
                Div({
                    style {
                        property("width", "8px")
                        property("height", "8px")
                        property("border-radius", "4px")
                        property("background-color", WLColors.Error)
                        property("margin-top", "8px")
                        property("flex-shrink", "0")
                    }
                })
                Div({ style { flexCol(gapPx = 2); property("flex", "1") } }) {
                    Span({ style { typography(TextLevel.TitleSmall); fg(WLColors.Error) } }) { Text(u.element) }
                    if (u.issue.isNotBlank()) {
                        Span({ style { typography(TextLevel.BodySmall); fg(WLColors.OnSurfaceVariant) } }) { Text(u.issue) }
                    }
                }
            }
        }
    }
}
